import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  DownloadIcon,
  SendIcon,
  Edit2Icon,
  Trash2Icon,
  MoreVerticalIcon,
  FileTextIcon,
  CalendarIcon,
  UserIcon,
  PhoneIcon,
  MailIcon,
  BuildingIcon,
  LoaderIcon,
} from 'lucide-react';
import { useInvoice, useDeleteInvoice, useSendInvoice, getInvoicePDFUrl } from '@/hooks/useInvoices';
import { InvoiceStatusBadge } from '@/components/invoices/InvoiceStatusBadge';
import { BottomNav } from '@/components/BottomNav';
import { getAccessToken } from '@/lib/supabase';

export function InvoiceDetail() {
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { data: invoice, isLoading } = useInvoice(invoiceId);
  const deleteMutation = useDeleteInvoice();
  const sendMutation = useSendInvoice();

  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: invoice?.currency || 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleDownloadPDF = async () => {
    if (!invoice || downloadingPDF) return;

    setDownloadingPDF(true);
    try {
      // Get access token for authentication
      const token = await getAccessToken();
      if (!token) {
        setNotice({ type: 'error', text: 'Please log in to download invoice' });
        return;
      }

      // Fetch PDF with authentication
      const pdfUrl = getInvoicePDFUrl(invoice.id);
      const response = await fetch(pdfUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setNotice({ type: 'success', text: 'PDF downloaded successfully' });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setNotice({ type: 'error', text: 'Failed to download PDF' });
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!invoice) return;

    try {
      await sendMutation.mutateAsync(invoice.id);
      setNotice({ type: 'success', text: 'Invoice sent via WhatsApp successfully' });
      setMenuOpen(false);
    } catch (error: any) {
      console.error('Error sending invoice:', error);
      setNotice({
        type: 'error',
        text: error?.message || 'Failed to send invoice via WhatsApp',
      });
    }
  };

  const handleDelete = async () => {
    if (!invoice) return;

    try {
      await deleteMutation.mutateAsync(invoice.id);
      setNotice({ type: 'success', text: 'Invoice deleted successfully' });
      setTimeout(() => {
        navigate('/payments');
      }, 1000);
    } catch (error: any) {
      console.error('Error deleting invoice:', error);
      setNotice({ type: 'error', text: 'Failed to delete invoice' });
    } finally {
      setDeleteConfirm(false);
      setMenuOpen(false);
    }
  };

  // Auto-dismiss notice
  React.useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pb-16">
        <LoaderIcon className="w-8 h-8 animate-spin text-[#5B8E87]" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16">
        <div className="bg-white p-4 border-b border-gray-200">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
        </div>
        <div className="p-4">
          <div className="text-center text-gray-500">Invoice not found</div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>

          {/* Actions Menu */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <MoreVerticalIcon className="w-5 h-5 text-gray-600" />
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                  {invoice.status === 'draft' && (
                    <button
                      onClick={() => {
                        navigate(`/invoices/${invoice.id}/edit`);
                        setMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Edit2Icon className="w-4 h-4" />
                      <span>Edit</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setDeleteConfirm(true);
                      setMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2Icon className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Notice Banner */}
      {notice && (
        <div
          className={`mx-4 mt-4 p-3 rounded-lg ${
            notice.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          <p className="text-sm">{notice.text}</p>
        </div>
      )}

      {/* Invoice Content */}
      <div className="p-4 space-y-4">
        {/* Invoice Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <FileTextIcon className="w-5 h-5 text-gray-400" />
                <h1 className="text-xl font-bold text-gray-900">
                  {invoice.invoice_number}
                </h1>
              </div>
              <p className="text-sm text-gray-500">Tax Invoice</p>
            </div>
            <InvoiceStatusBadge status={invoice.status} />
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Invoice Date</span>
              <p className="font-medium text-gray-900">
                {formatDate(invoice.invoice_date)}
              </p>
            </div>
            {invoice.due_date && (
              <div>
                <span className="text-gray-500">Due Date</span>
                <p className="font-medium text-gray-900">
                  {formatDate(invoice.due_date)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Client Details Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Billed To</h2>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <UserIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-900">
                {invoice.client_business_name || invoice.client_name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <PhoneIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">{invoice.client_phone}</span>
            </div>
            {invoice.client_email && (
              <div className="flex items-center gap-2">
                <MailIcon className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">{invoice.client_email}</span>
              </div>
            )}
            {invoice.client_gstin && (
              <div className="flex items-center gap-2">
                <BuildingIcon className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-600">
                  GSTIN: {invoice.client_gstin}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Service Details Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Service Details</h2>
          <p className="text-sm text-gray-900 mb-2">{invoice.service_description}</p>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>HSN/SAC: {invoice.hsn_sac_code}</span>
            <span>Tax Rate: {invoice.tax_rate}%</span>
          </div>
        </div>

        {/* Amount Breakdown Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Amount Breakdown</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Base Amount:</span>
              <span>{formatCurrency(invoice.amount)}</span>
            </div>
            {invoice.cgst_amount > 0 && (
              <>
                <div className="flex justify-between text-gray-600">
                  <span>CGST ({invoice.tax_rate / 2}%):</span>
                  <span>{formatCurrency(invoice.cgst_amount)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>SGST ({invoice.tax_rate / 2}%):</span>
                  <span>{formatCurrency(invoice.sgst_amount)}</span>
                </div>
              </>
            )}
            {invoice.igst_amount > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>IGST ({invoice.tax_rate}%):</span>
                <span>{formatCurrency(invoice.igst_amount)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-200 font-bold text-[#5B8E87] text-lg">
              <span>Total:</span>
              <span>{formatCurrency(invoice.total_amount)}</span>
            </div>
          </div>
        </div>

        {/* Notes (if any) */}
        {invoice.notes && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Notes</h2>
            <p className="text-sm text-gray-600">{invoice.notes}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleDownloadPDF}
            disabled={downloadingPDF}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-[#5B8E87] border-2 border-[#5B8E87] rounded-lg font-medium hover:bg-[#5B8E87] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloadingPDF ? (
              <>
                <LoaderIcon className="w-5 h-5 animate-spin" />
                <span>Downloading...</span>
              </>
            ) : (
              <>
                <DownloadIcon className="w-5 h-5" />
                <span>Download PDF</span>
              </>
            )}
          </button>

          <button
            onClick={handleSendWhatsApp}
            disabled={sendMutation.isPending}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#5B8E87] text-white rounded-lg font-medium hover:bg-[#4A7269] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendMutation.isPending ? (
              <LoaderIcon className="w-5 h-5 animate-spin" />
            ) : (
              <SendIcon className="w-5 h-5" />
            )}
            <span>{sendMutation.isPending ? 'Sending...' : 'Send via WhatsApp'}</span>
          </button>
        </div>

        {/* Metadata */}
        {(invoice.sent_at || invoice.payment_id) && (
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="space-y-1 text-xs text-gray-500">
              {invoice.sent_at && (
                <div>
                  Sent on: {new Date(invoice.sent_at).toLocaleString('en-IN')}
                </div>
              )}
              {invoice.payment_id && (
                <div>Linked to payment: {invoice.payment_id.slice(0, 8)}...</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Invoice?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete this invoice? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleteMutation.isPending && (
                  <LoaderIcon className="w-4 h-4 animate-spin" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
